/**
 * AI CONTEXT
 * Purpose: single exception row with attendance control + context modal (tryout/makeup/student).
 * Handles: exception-type-specific status updates and session mapping.
 * Depends on: Firebase attendance mutator + exception data typing guards.
 * Used by: ExceptionList.
 * Refactor: extract exception-type dispatch logic to typed handler map.
 */
import { ReactNode, useCallback, useMemo, useState } from "react"

import { classSessionAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Log from "#classes/Log"
import { CLASS_GROUPS } from "#constants/classOptions"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { useFirebaseContext } from "#contexts/FirebaseContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { type AttendanceStatus, invariant } from "#customTypes/types"
import AddMakeupFormModal from "#forms/AddMakeup/AddMakeupFormModal"
import AddStudentFormModal from "#forms/AddStudent/AddStudentFormModal"
import SwitchClassButton from "#forms/AddStudent/SwitchClassButton"
import TryoutProgressFormModal from "#forms/TryoutProgressForm/TryoutProgressFormModal"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import { type ExceptionStudentData } from "#hooks/newHooks/useExceptionListData"
import { getSessionInfoFromId } from "#utils/classHelpers"
import useIsSuperView from "#views/SuperView/useIsSuperView"

import AttendanceRow from "./AttendanceView/AttendanceRow"

const isMakeup = (
  exceptionType: ExceptionStudentData["exceptionType"],
  _exceptionData: ExceptionStudentData["exceptionData"]
): _exceptionData is MakeupConstructor => {
  return exceptionType === "makeup"
}

const isTryout = (
  exceptionType: ExceptionStudentData["exceptionType"],
  _exceptionData: ExceptionStudentData["exceptionData"]
): _exceptionData is TryoutConstructor => {
  return exceptionType === "tryout"
}

const isAssistant = (
  exceptionType: ExceptionStudentData["exceptionType"],
  _exceptionData: ExceptionStudentData["exceptionData"]
): _exceptionData is undefined => {
  return exceptionType === "assistant"
}

const ExceptionListItem = ({
  item: exceptionStudent,
}: {
  item: ExceptionStudentData
}) => {
  const isSuperView = useIsSuperView()

  const { onStudentAttendanceChange } = useFirebaseContext()
  const { exceptionType, student, exceptionData, sessionId } = exceptionStudent
  const [classId, sessionIndex] = getSessionInfoFromId(sessionId)
  const [modalContent, setModalContent] = useState<ReactNode | null>(null)

  const projectSessionId =
    exceptionType === "makeup" &&
    exceptionData &&
    "missedSessionId" in exceptionData
      ? (exceptionData.missedSessionId as Uuid)
      : sessionId

  const projectSession = useOneDoc(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    projectSessionId
  )
  const projectId = projectSession?.projectId

  const studentForDisplay = useMemo(() => {
    let status: AttendanceStatus | undefined
    // Get actual attendance status from student.attendance[classId][sessionIndex]
    const studentAttendance =
      exceptionType === "assistant"
        ? student.assistantAttendance
        : student?.attendance

    const attendanceKey =
      exceptionType === "assistant" ? "assistantAttendance" : "attendance"

    if (!studentAttendance) {
      status = ATTENDANCE_STATUSES.INVALID
    } else if (exceptionType === "makeup") {
      invariant(
        !!exceptionData && "missedSessionId" in exceptionData,
        `Makeup exception data not found for student ${Log.student(student)}`
      )
      const missedSessionId = exceptionData.missedSessionId as Uuid
      const [missedClassId, missedSessionIndex] =
        getSessionInfoFromId(missedSessionId)

      status =
        student?.[attendanceKey]?.[missedClassId]?.[missedSessionIndex]?.status
    } else {
      status = student?.[attendanceKey]?.[classId]?.[sessionIndex]?.status
    }

    return {
      ...student,
      exceptionType,
      exceptionData,
      attendanceStatus: status,
    }
  }, [student, exceptionType, classId, sessionIndex, exceptionData])

  const handleCloseModal = () => {
    setModalContent(null)
  }

  const handleStudentClick = () => {
    if (isTryout(exceptionType, exceptionData)) {
      setModalContent(
        <TryoutProgressFormModal
          tryoutId={exceptionData?.id}
          onClose={handleCloseModal}
        />
      )
    } else if (isMakeup(exceptionType, exceptionData)) {
      setModalContent(
        <AddMakeupFormModal
          onClose={handleCloseModal}
          makeupId={exceptionData?.id}
        />
      )
    } else if (isAssistant(exceptionType, exceptionData)) {
      setModalContent(
        <AddStudentFormModal
          onClose={handleCloseModal}
          studentId={student.id}
        />
      )
    } else {
      throw new Error(
        `Invalid exception type ${exceptionType} for ${Log.student(student)}`
      )
    }
  }

  const handleAttendanceChange = useCallback(
    (student: StudentConstructor, newStatus: AttendanceStatus) => {
      if (exceptionType === "makeup") {
        invariant(
          !!exceptionData && "missedSessionId" in exceptionData,
          `Makeup exception data not found for student ${Log.student(student)}`
        )
        const typeSafeExceptionData = exceptionData as MakeupConstructor
        const [missedClassId, missedSessionIndex] = getSessionInfoFromId(
          typeSafeExceptionData.missedSessionId
        )
        return onStudentAttendanceChange(
          student,
          missedClassId,
          missedSessionIndex,
          newStatus,
          "attendance",
          projectId
        )
      }

      if (exceptionType === "assistant") {
        return onStudentAttendanceChange(
          student,
          classId,
          sessionIndex,
          newStatus,
          "assistantAttendance"
        )
      }

      onStudentAttendanceChange(
        student,
        classId,
        sessionIndex,
        newStatus,
        "attendance",
        projectId
      )
    },
    [
      exceptionType,
      exceptionData,
      classId,
      sessionIndex,
      projectId,
      onStudentAttendanceChange,
    ]
  )

  return (
    <>
      <Flex
        grow
        style={{
          width: isSuperView ? 370 : 500,
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <Flex
          grow
          style={{
            width: isSuperView ? 270 : "100%",
          }}
        >
          <AttendanceRow
            student={studentForDisplay}
            status={
              studentForDisplay.attendanceStatus ?? ATTENDANCE_STATUSES.INVALID
            }
            sessionId={sessionId}
            classGroup={CLASS_GROUPS.DIGITAL_ART}
            onStatusChange={handleAttendanceChange}
            size={isSuperView ? "small" : undefined}
            onStudentClick={handleStudentClick}
          />
          {exceptionType === "assistant" && (
            <SwitchClassButton
              studentId={student.id}
              oldClassId={classId}
              isAssistant={isAssistant(exceptionType, exceptionData)}
            />
          )}
        </Flex>
      </Flex>
      {modalContent}
    </>
  )
}

export default ExceptionListItem
